import React from 'react';
import { GlassGradient, GlassPanel, BattenteTriangleFill, AntaRibaltaFill, FixedMark } from './primitives';

// Panel opening codes: f = fisso, bs/bd = battente SX/DX, as/ad = anta-ribalta SX/DX
const drawOpening = (code, x, y, w, h, key) => {
  switch (code) {
    case 'bs': return <BattenteTriangleFill key={key} x={x} y={y} w={w} h={h} side="left" />;
    case 'bd': return <BattenteTriangleFill key={key} x={x} y={y} w={w} h={h} side="right" />;
    case 'as': return <AntaRibaltaFill key={key} x={x} y={y} w={w} h={h} side="left" />;
    case 'ad': return <AntaRibaltaFill key={key} x={x} y={y} w={w} h={h} side="right" />;
    default: return <FixedMark key={key} x={x} y={y} w={w} h={h} />;
  }
};

/**
 * Multi-panel window/French door icon. Portafinestre use a taller frame (floor to
 * ceiling) so they read differently from windows with the same number of panels.
 */
const makePanelsIcon = (gradId, codes, { pf = false } = {}) => {
  const n = codes.length;
  const panelW = pf ? 30 : 32;
  const gap = 4;
  const W = 8 + n * panelW + (n - 1) * gap + 8;
  const H = pf ? 120 : 100;
  const y = pf ? 6 : 8;
  const h = H - 2 * y;
  return () => (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
      <GlassGradient id={gradId} />
      {codes.map((_, i) => (
        <GlassPanel key={`p${i}`} x={8 + i * (panelW + gap)} y={y} w={panelW} h={h} gradId={gradId} />
      ))}
      <rect x={4} y={4} width={W - 8} height={H - 8} fill="none" stroke="#222" strokeWidth="2.5" />
      {codes.map((c, i) => drawOpening(c, 8 + i * (panelW + gap), y, panelW, h, `o${i}`))}
    </svg>
  );
};

// ====== PORTAFINESTRE 3 ANTE ======
export const open_pf3_fff = makePanelsIcon('g_pf3fff', ['f', 'f', 'f'], { pf: true });
export const open_pf3_bsf_bd = makePanelsIcon('g_pf3bfb', ['bs', 'f', 'bd'], { pf: true });
export const open_pf3_ar_f_ar = makePanelsIcon('g_pf3afa', ['as', 'f', 'ad'], { pf: true });
export const open_pf3_f_bs_bd = makePanelsIcon('g_pf3fbb', ['f', 'bs', 'bd'], { pf: true });
export const open_pf3_bs_bd_f = makePanelsIcon('g_pf3bbf', ['bs', 'bd', 'f'], { pf: true });

// ====== PORTAFINESTRE 4 ANTE ======
export const open_pf4_batt_batt_batt_batt = makePanelsIcon('g_pf4bbbb', ['bs', 'bd', 'bs', 'bd'], { pf: true });
export const open_pf4_ar_ar_ar_ar = makePanelsIcon('g_pf4aaaa', ['as', 'ad', 'as', 'ad'], { pf: true });
export const open_pf4_fisso_batt_batt_fisso = makePanelsIcon('g_pf4fbbf', ['f', 'bs', 'bd', 'f'], { pf: true });
export const open_pf4_batt_fisso_fisso_batt = makePanelsIcon('g_pf4bffb', ['bs', 'f', 'f', 'bd'], { pf: true });

// ====== PIEGHEVOLI (a libro) ======
// Panels with zig-zag fold lines between them, so 2/3/4/6 ante read differently.
const makeFoldingIcon = (gradId, n) => {
  const panelW = n > 4 ? 20 : 28;
  const W = 8 + n * panelW + 8;
  return () => (
    <svg viewBox={`0 0 ${W} 100`} className="w-full h-full">
      <GlassGradient id={gradId} />
      {Array.from({ length: n }).map((_, i) => (
        <GlassPanel key={`p${i}`} x={8 + i * panelW} y={8} w={panelW} h={84} gradId={gradId} />
      ))}
      <rect x={4} y={4} width={W - 8} height={92} fill="none" stroke="#222" strokeWidth="2.5" />
      <polyline
        points={Array.from({ length: n + 1 }).map((_, i) => `${8 + i * panelW},${i % 2 ? 22 : 78}`).join(' ')}
        fill="none" stroke="#0055CC" strokeWidth="1" strokeDasharray="4,3"
      />
    </svg>
  );
};

export const folding2 = makeFoldingIcon('g_fold2', 2);
export const folding3 = makeFoldingIcon('g_fold3', 3);
export const folding4 = makeFoldingIcon('g_fold4', 4);
export const folding6 = makeFoldingIcon('g_fold6', 6);

// ====== PANNELLO PORTA D'INGRESSO ======
export const doorPanelCieco = () => (
  <svg viewBox="0 0 70 120" className="w-full h-full">
    <rect x={8} y={8} width={54} height={104} fill="#8A6A4F" stroke="#333" strokeWidth="1" />
    <rect x={15} y={16} width={40} height={38} fill="none" stroke="#6E523C" strokeWidth="1.5" />
    <rect x={15} y={64} width={40} height={40} fill="none" stroke="#6E523C" strokeWidth="1.5" />
    <rect x={4} y={4} width={62} height={112} fill="none" stroke="#222" strokeWidth="2.5" />
    <rect x={52} y={56} width={4} height={12} rx={1} fill="#D9D9D9" />
  </svg>
);

export const doorPanelVetro = () => (
  <svg viewBox="0 0 70 120" className="w-full h-full">
    <GlassGradient id="g_dpv" />
    <rect x={8} y={8} width={54} height={104} fill="#8A6A4F" stroke="#333" strokeWidth="1" />
    <GlassPanel x={17} y={16} w={36} h={58} gradId="g_dpv" />
    <rect x={15} y={82} width={40} height={22} fill="none" stroke="#6E523C" strokeWidth="1.5" />
    <rect x={4} y={4} width={62} height={112} fill="none" stroke="#222" strokeWidth="2.5" />
    <rect x={52} y={56} width={4} height={12} rx={1} fill="#D9D9D9" />
  </svg>
);
